// Tenantra Inc. - Back office: monthly processing fee revenue by payment method
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api, { apiHelpers } from '@/services/api';
import { SortableHeader, useSort, RowOpenButton, ExportButton, exportCsv, TableSkeleton } from '@/components/dashboard/TableKit';
import { DetailSheet, Section, fmtDate } from '@/components/dashboard/DetailPanel';

const money = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const months = () => Array.from({ length: 12 }, (_, i) => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i);
  return { value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' }) };
});
const COLUMNS = [
  { key: 'label', label: 'Method', span: 4 },
  { key: 'count', label: 'Payments', span: 2, align: 'right' },
  { key: 'volume', label: 'Rent volume', span: 2, align: 'right' },
  { key: 'fees', label: 'Fee revenue', span: 2, align: 'right' },
  { key: 'open', label: '', span: 2, sortable: false },
];
const ACCESSORS = { label: r => r.label, count: r => r.count, volume: r => r.volume, fees: r => r.fees };
const STATUS = { settled: 'Paid', processing: 'Processing', requested: 'Interac requested' };

function Tiles({ totals }) {
  const avg = totals.count ? totals.fees / totals.count : 0;
  const transferred = totals.fees_transferred || 0;
  const tiles = [
    { id: 'count', label: 'Payments', value: totals.count, note: totals.count ? 'Through Tenantra this month' : 'No payments this month' },
    { id: 'volume', label: 'Rent volume', value: money(totals.volume), note: 'Rent collected, before fees' },
    { id: 'fees', label: 'Fee revenue', value: money(totals.fees), note: totals.fees ? 'Processing fees charged' : 'No fees charged', ok: totals.fees > 0 },
    { id: 'transferred', label: 'Fees transferred', value: money(transferred), note: transferred ? 'Swept to Tenantra wallet' : 'None swept yet', ok: transferred > 0 },
    { id: 'avg', label: 'Average fee', value: money(avg), note: 'Per payment' },
    { id: 'quebec', label: 'Quebec waived', value: totals.quebec_waived, note: totals.quebec_waived ? 'Credit card payments, $0 fee' : 'None this month' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-y-5" data-testid="fee-revenue-stats">
      {tiles.map(t => (
        <div key={t.id} className="pr-3 sm:pr-4 lg:border-r lg:last:border-0 border-slate-200 lg:pl-4 lg:first:pl-0" data-testid={`fee-stat-${t.id}`}>
          <p className="text-slate-500 text-xs mb-0.5">{t.label}</p>
          <p className="text-2xl font-bold tabular-nums text-slate-900" data-testid={`fee-stat-${t.id}-value`}>{t.value}</p>
          <p className={`text-xs mt-1 ${t.ok ? 'text-teal-700' : 'text-slate-400'}`}>{t.note}</p>
        </div>
      ))}
    </div>
  );
}

function MethodSheet({ month, row, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!row) return;
    setItems(null);
    api.get(`/admin/payment-fees/revenue/${month}/${row.method}`).then(r => setItems(r.data.items)).catch(e => { toast.error(apiHelpers.handleError(e)); setItems([]); });
  }, [month, row]);
  return (
    <DetailSheet open={!!row} onClose={onClose} loading={!items} crumb="Fee revenue" title={row?.label} testId="fee-method-sheet"
      meta={row && <span className="text-slate-500">{row.count} payments · {money(row.fees)} in fees</span>}>
      <Section title="Payments" aside={months().find(m => m.value === month)?.label}>
        {items?.length === 0 ? <p className="text-sm text-slate-500">No payments by this method this month.</p> : (
          <ul className="divide-y divide-slate-100 -mx-1">
            {items?.map(p => (
              <li key={p.id} className="px-1 py-2.5 flex items-start justify-between gap-3 text-sm" data-testid={`fee-payment-${p.id}`}>
                <div className="min-w-0">
                  <p className="text-slate-800 font-medium truncate">{p.tenant_name}</p>
                  <p className="text-[11px] text-slate-400">{fmtDate(p.created_at)} · {p.reference || 'No reference'} · {STATUS[p.status] || p.status}{p.province ? ` · ${p.province}` : ''}</p>
                </div>
                <div className="text-right tabular-nums shrink-0">
                  <p className="text-slate-800">{money(p.amount)}</p>
                  <p className={`text-[11px] ${p.fee > 0 ? 'text-teal-700' : 'text-slate-400'}`}>{p.fee > 0 ? `+${money(p.fee)} fee` : p.province === 'QC' && row?.method === 'credit_card' ? 'Fee waived (QC)' : 'No fee'}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </DetailSheet>
  );
}

export function FeeRevenuePanel() {
  const options = useMemo(months, []);
  const [month, setMonth] = useState(options[0].value);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    setData(null);
    api.get('/admin/payment-fees/revenue', { params: { month } }).then(r => setData(r.data)).catch(e => toast.error(apiHelpers.handleError(e)));
  }, [month]);
  const { sorted, sort, toggle } = useSort(data?.items || [], ACCESSORS);
  const methodLabel = useMemo(() => Object.fromEntries((data?.items || []).map(i => [i.method, i.label])), [data]);
  const label = options.find(m => m.value === month)?.label;
  const exportRows = () => exportCsv(`tenantra-fee-revenue-${month}.csv`, [...sorted, { label: 'Total', ...data.totals }], [
    { label: 'Month', value: () => month }, { label: 'Method', value: r => r.label },
    { label: 'Payments', value: r => r.count }, { label: 'Rent volume (CAD)', value: r => r.volume.toFixed(2) },
    { label: 'Fee revenue (CAD)', value: r => r.fees.toFixed(2) }, { label: 'Quebec fee waived', value: r => r.quebec_waived },
  ]);

  return (
    <div className="space-y-6" data-testid="fee-revenue-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Fee Revenue</h1>
          <p className="text-sm text-slate-500">Processing fees tenants paid in {label}, by payment method.</p>
        </div>
        <div className="flex items-center gap-4">
          <ExportButton onClick={exportRows} disabled={!data} testId="fee-revenue-export" />
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="h-9 w-48 text-sm bg-white" data-testid="fee-revenue-month"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{options.map(m => <SelectItem key={m.value} value={m.value} data-testid={`fee-month-${m.value}`}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {!data ? <TableSkeleton rows={5} spans={[4, 2, 2, 2, 2]} /> : (
        <>
          <Tiles totals={data.totals} />
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="fee-revenue-table">
            <SortableHeader columns={COLUMNS} sort={sort} onSort={toggle} sticky={false} />
            <div className="divide-y divide-slate-100">
              {sorted.map(r => (
                <div key={r.method} onClick={() => setOpen(r)} className="grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-slate-50 cursor-pointer" data-testid={`fee-revenue-row-${r.method}`}>
                  <div className="col-span-8 sm:col-span-4 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{r.label}</p>
                    <p className="text-[11px] text-slate-400 sm:hidden">{r.count} payments · {money(r.fees)} fees</p>
                    {r.quebec_waived > 0 && <p className="text-[11px] text-slate-400">{r.quebec_waived} in Quebec, fee waived</p>}
                  </div>
                  <div className="hidden sm:block col-span-2 text-right text-sm tabular-nums text-slate-700" data-testid={`fee-revenue-${r.method}-count`}>{r.count}</div>
                  <div className="hidden sm:block col-span-2 text-right text-sm tabular-nums text-slate-700">{money(r.volume)}</div>
                  <div className={`hidden sm:block col-span-2 text-right text-sm tabular-nums font-medium ${r.fees > 0 ? 'text-teal-700' : 'text-slate-400'}`} data-testid={`fee-revenue-${r.method}-fees`}>{money(r.fees)}</div>
                  <div className="col-span-4 sm:col-span-2 flex justify-end"><RowOpenButton onClick={() => setOpen(r)} testId={`fee-revenue-open-${r.method}`} label={`Open ${r.label} payments`} /></div>
                </div>
              ))}
              <div className="grid grid-cols-12 gap-3 px-4 py-3 items-center bg-slate-50 font-semibold text-sm" data-testid="fee-revenue-totals">
                <div className="col-span-8 sm:col-span-4 text-slate-900">Total</div>
                <div className="hidden sm:block col-span-2 text-right tabular-nums">{data.totals.count}</div>
                <div className="hidden sm:block col-span-2 text-right tabular-nums">{money(data.totals.volume)}</div>
                <div className="col-span-4 sm:col-span-2 text-right tabular-nums text-teal-700" data-testid="fee-revenue-total-fees">{money(data.totals.fees)}</div>
              </div>
            </div>
          </div>
          {data.flagged?.length > 0 && (
            <div className="bg-white border border-amber-300 rounded-lg overflow-hidden" data-testid="fee-revenue-flagged">
              <div className="px-4 py-3 border-b border-amber-200 bg-amber-50">
                <p className="text-sm font-semibold text-amber-900">Flagged for review · {data.flagged.length}</p>
                <p className="text-[11px] text-amber-700">Rent returned or charged back after the processing fee was swept. No auto-reversal — review manually.</p>
              </div>
              <div className="divide-y divide-amber-100">
                {data.flagged.map(f => (
                  <div key={f.id} className="grid grid-cols-12 gap-3 px-4 py-3 items-center text-sm" data-testid={`fee-flagged-${f.id}`}>
                    <div className="col-span-6 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{f.reference || 'No reference'}</p>
                      <p className="text-[11px] text-slate-400">{methodLabel[f.method] || f.method} · {STATUS[f.status] || f.status}</p>
                    </div>
                    <div className="col-span-3 text-right tabular-nums text-slate-700">{money(f.amount)}</div>
                    <div className="col-span-3 text-right tabular-nums text-amber-700">{money(f.fee)} fee swept</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      <MethodSheet month={month} row={open} onClose={() => setOpen(null)} />
    </div>
  );
}
